// ServicesSection.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Activity,
  Users,
  Calendar,
  Heart,
  Stethoscope,
  HeartPulse,
} from 'lucide-react';

export const ServicesSection = () => {
  const navigate = useNavigate();

  const featuredServices = [
    {
      icon: Activity,
      title: 'Emergency Services',
      description: '24/7 emergency medical care with rapid response teams for critical situations.',
      color: 'from-red-500 to-red-700',
    },
    {
      icon: Users,
      title: 'Out Patient Department',
      description: 'Comprehensive medical consultations and treatments without hospital admission.',
      color: 'from-blue-500 to-blue-700',
    },
    {
      icon: Calendar,
      title: 'Daycare Services',
      description: 'Same-day medical procedures allowing you to return home on the same day.',
      color: 'from-green-500 to-green-700',
    },
    {
      icon: Heart,
      title: 'ICU Services',
      description: 'Advanced critical care with state-of-the-art monitoring equipment.',
      color: 'from-orange-500 to-orange-700',
    },
    {
      icon: Stethoscope,
      title: 'Laboratory Services',
      description: 'Comprehensive diagnostic testing with advanced laboratory equipment.',
      color: 'from-teal-500 to-teal-700',
    },
    {
      icon: HeartPulse,
      title: 'ECG Services',
      description: 'Electrocardiogram testing for heart health assessment and diagnosis.',
      color: 'from-emerald-500 to-emerald-700',
    },
  ];

  return (
    <section id="services" className="py-20 bg-[hsl(var(--background))]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
            <Activity className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold text-[hsl(var(--primary))]">
              Our Medical Services
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
            Comprehensive Healthcare
            <span className="block mt-2 text-gradient">Services</span>
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            We provide a complete range of medical services with modern facilities and 
            experienced healthcare professionals for all your health needs.
          </p>
        </div>

        {/* Featured Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {featuredServices.map((service, index) => (
            <Card
              key={index}
              className="group hover:shadow-[var(--shadow-xl)] transition-all duration-300 hover:scale-105 border-none overflow-hidden bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--muted))]">
              <CardHeader className="space-y-4">
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-[var(--shadow-md)] group-hover:shadow-[var(--shadow-lg)] transition-all duration-300 group-hover:scale-110`}
                >
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-[hsl(var(--muted-foreground))]">
                  {service.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="text-center">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">24/7</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Emergency Care</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">50+</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Medical Staff</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">10+</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Services</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">100%</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Patient Focused</div>
          </div>
        </div>

        {/* See More Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/services')}
            className="bg-[hsl(var(--primary))] text-white px-8 py-3 rounded-full font-semibold hover:bg-[hsl(var(--primary-dark))] transition-colors inline-flex items-center space-x-2 group"
          >
            <span>View All Services</span>
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};